import { useRef, useState } from "react";
import { BookOpen, Images } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const month = new Date().toLocaleDateString("en-AU", { month: "long", year: "numeric" });

export const CatalogueButton = ({ category }) => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({ prices: true, in_stock: false, only_cat: true });
  const [busy, setBusy] = useState(false);

  const go = async () => {
    setBusy(true);
    const p = new URLSearchParams({ prices: opts.prices, in_stock: opts.in_stock, category: category && opts.only_cat ? category : "" });
    try {
      await downloadFile(`/catalogue/pdf?${p}`, `GS_Pacific_Catalogue.pdf`);
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const Opt = ({ k, label }) => (
    <div className="flex items-center justify-between py-2">
      <Label>{label}</Label>
      <Switch checked={opts[k]} onCheckedChange={(v) => setOpts({ ...opts, [k]: v })} data-testid={`catalogue-opt-${k}`} />
    </div>
  );

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="catalogue-btn"><BookOpen className="mr-1 h-4 w-4" /> Product catalogue</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="catalogue-dialog">
          <DialogHeader>
            <DialogTitle>Product Catalogue — {month}</DialogTitle>
            <DialogDescription>A visual PDF with images, descriptions and prices for your retailers.</DialogDescription>
          </DialogHeader>
          <div className="divide-y divide-slate-100">
            <Opt k="prices" label="Show prices" />
            <Opt k="in_stock" label="Only items in stock" />
            {category && <Opt k="only_cat" label={`Only "${category}" category`} />}
          </div>
          <DialogFooter>
            <Button disabled={busy} onClick={go} className="bg-[#01195D] hover:bg-[#0A2A7A]" data-testid="catalogue-download-btn">{busy ? "Building PDF…" : "Download PDF"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const BulkImagesButton = ({ onDone }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const { data } = await api.post("/items/images/bulk", fd);
      toast.success(`${data.matched.length} image(s) matched to items`);
      if (data.unmatched.length) toast.warning(`No SKU found for: ${data.unmatched.slice(0, 5).join(", ")}${data.unmatched.length > 5 ? "…" : ""}`);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid="bulk-images-input" />
      <Button variant="outline" disabled={busy} onClick={() => ref.current.click()} title="Name each file by SKU, e.g. ABC-001.jpg" data-testid="bulk-images-btn">
        <Images className="mr-1 h-4 w-4" /> {busy ? "Uploading…" : "Bulk images"}
      </Button>
    </>
  );
};
